/**
 * User Settings types.
 *
 * @class UserSettingsTypes
 * @name types
 * @memberof Mojito.Services.UserSettings
 */
export default class UserSettingsTypes {}

/**
 * Available odds formats.
 *
 * @readonly
 * @enum {string}
 * @memberof Mojito.Services.UserSettings.types
 */
UserSettingsTypes.ODDS_FORMAT = {
    DECIMAL: 'decimal',
    FRACTIONAL: 'fractional',
    AMERICAN: 'american',
};

/**
 * Supported odds formats.
 *
 * @typedef ODDS_FORMATS_SUPPORTED
 * @memberof Mojito.Services.UserSettings.types
 */
UserSettingsTypes.ODDS_FORMATS_SUPPORTED = Object.values(UserSettingsTypes.ODDS_FORMAT);

/**
 * Supported onboarding types.
 *
 * @readonly
 * @enum {string}
 * @memberof Mojito.Services.UserSettings.types
 * @public
 */
UserSettingsTypes.ONBOARDING_TYPES = {
    EVENTS_QUICK_NAVIGATION: 'eventsQuickNavigation',
    LEAGUES_QUICK_NAVIGATION: 'leaguesQuickNavigation',
    BETSLIP_FREE_BET_HINT: 'betslipFreeBetHint',
    MATCH_ACCA: 'matchAcca',
    FAST_MARKETS: 'fastMarkets',
    SETTLED_BETS_TAB: 'settledBetsTab',
    QUICK_START_GUIDE_BANNER: 'quickStartGuideBanner',
    INPLAY_FAVORITES: 'inplayFavorites',
};

/**
 * Onboarding types which are initially enabled.
 * Some should be enabled depending on configuration or navigation to a specific page.
 *
 * @typedef ONBOARDINGS_INITIALLY_ENABLED
 * @memberof Mojito.Services.UserSettings.types
 * @public
 */
UserSettingsTypes.ONBOARDINGS_INITIALLY_ENABLED = {
    [UserSettingsTypes.ONBOARDING_TYPES.LEAGUES_QUICK_NAVIGATION]: true,
    [UserSettingsTypes.ONBOARDING_TYPES.EVENTS_QUICK_NAVIGATION]: true,
};

/**
 * Onboarding types which are allowed for a new user.
 * Usually, onboarding is shown for user who previously used the site, but not a feature.
 *
 * @typedef ONBOARDINGS_FOR_NEW_USER
 * @memberof Mojito.Services.UserSettings.types
 * @public
 */
UserSettingsTypes.ONBOARDINGS_FOR_NEW_USER = {};

/**
 * User settings service layer config.
 *
 * @typedef UserSettingsConfig
 * @type {object}
 * @property {Mojito.Services.UserSettings.types.ODDS_FORMAT} oddsFormat - The format of odds presentation.
 * @property {Mojito.Services.UserSettings.types.DateTimeFormat} dateTimeFormat - Date time format options.
 * @property {string} countryCode - Two letter ISO 3166-1 alpha-2 country code.
 *
 * @memberof Mojito.Services.UserSettings.types
 */

/**
 * Date time format options.
 * Used for date and time formatting across application based on defined preferences, such as user locale, selected GMT time offset or time zone, etc.
 *
 * @typedef DateTimeFormat
 * @type {object}
 * @property {string|string[]} locales - A string with a BCP 47 language tag, or an array of such strings.
 * @property {string} timeZone - IANA time zone name, e.g. `Europe/Stockholm`. Takes precedence over <code>timeOffset</code>.
 * @property {number|string} timeOffset - Numeric offset from GMT (-12 - 12) or string 'localTime' if system time should be used. This property will be ignored if `timeZone` is set.
 *
 * @memberof Mojito.Services.UserSettings.types
 */

/**
 * Sport segmentation priority type.
 *
 * @readonly
 * @enum {string}
 * @memberof Mojito.Services.UserSettings.types
 * @public
 */
UserSettingsTypes.PRIORITY_TYPES = {
    /** First priority means that elements will be moved to the beginning of list. */
    FIRST: 'first',
    /** Second priority means that elements will be moved after the first priority elements(elements with the highest display order value). */
    SECOND: 'second',
};

/**
 * User authentication roles used only under Build My Bet(BMB) application type.
 *
 * @readonly
 * @enum {string}
 * @memberof Mojito.Services.UserSettings.types
 */
UserSettingsTypes.AUTHENTICATION_ROLES = {
    /** Not authenticated Build my bet app type user(regular application user). */
    PUNTER: 'punter',
    /** Build my bet app type shop user who is authenticated by providing AUTH_TOKEN from mojito app wrapper(native, terminal, etc.). */
    SHOP: 'shop',
    /** Build my bet app type trader user who is authenticated using regular mojito login functionality. */
    TRADER: 'trader',
};
